import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import ScoreRing from '../components/ScoreRing';

export default function DashboardPage() {
  const { user } = useAuth();
  const [resume, setResume] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/resume/me').catch(() => null),
      api.get('/match/history').catch(() => null)
    ]).then(([r, h]) => {
      if (r) setResume(r.data.resume);
      if (h) setHistory(h.data.matches || []);
    }).finally(() => setLoading(false));
  }, []);

  const avgScore = history.length
    ? Math.round(history.reduce((sum, m) => sum + (m.result?.overallScore || 0), 0) / history.length)
    : 0;

  return (
    <div className="page">
      <div style={{ marginBottom: 40 }}>
        <div style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>
          Hey {user?.name?.split(' ')[0]} 👋
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>Your AI-powered resume dashboard</p>
      </div>

      {/* Stats */}
      <div className="three-col" style={{ marginBottom: 32 }}>
        {[
          { label: 'Resume', value: resume ? '✅ Uploaded' : '❌ Not uploaded', sub: resume?.filename || 'Upload to get started' },
          { label: 'Analyses', value: history.length, sub: 'Total matches run' },
          { label: 'Avg Score', value: avgScore || '–', sub: 'Across all analyses' }
        ].map(({ label, value, sub }) => (
          <div className="card" key={label} style={{ marginBottom: 0 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: 'var(--muted)', marginBottom: 8 }}>{label}</div>
            <div style={{ fontFamily: 'Syne', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-title">Quick Actions</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/upload" className="btn btn-primary">📄 {resume ? 'Update Resume' : 'Upload Resume'}</Link>
          <Link to="/analyze" className="btn btn-ghost">🔍 Analyze Match</Link>
          <Link to="/jobs" className="btn btn-ghost">💼 Browse Jobs</Link>
          <Link to="/history" className="btn btn-ghost">📊 View History</Link>
        </div>
      </div>

      {/* Recent analyses */}
      {history.length > 0 && (
        <div className="card">
          <div className="card-title">Recent Analyses</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {history.slice(0, 5).map(match => {
              const score = match.result?.overallScore || 0;
              const badgeCls = score >= 70 ? 'badge-green' : score >= 45 ? 'badge-yellow' : 'badge-red';
              return (
                <div key={match._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface2)', padding: '14px 16px', borderRadius: 10 }}>
                  <div>
                    <div style={{ fontFamily: 'Syne', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                      {match.customJobTitle || match.job?.title || 'Custom Analysis'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {match.customCompany || match.job?.company || '—'} · {new Date(match.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`badge ${badgeCls}`}>{score}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && history.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '60px 32px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
          <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No analyses yet</div>
          <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>Upload your resume and analyze it against a job description to get started.</p>
          <Link to={resume ? '/analyze' : '/upload'} className="btn btn-primary">
            {resume ? '🔍 Run First Analysis' : '📄 Upload Resume First'}
          </Link>
        </div>
      )}
    </div>
  );
}
